// client/src/components/HomePage.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import '../App.css';
import { sanitizePlayerName } from '../utils/sanitizer';
import PlayerCountModal from './PlayerCountModal';
import { useLanguage } from '../contexts/LanguageContext';
import LanguageSelector from './LanguageSelector';
import socket from '../utils/socket';
import SEOFooter from './SEOFooter';
import SEO from './SEO';
import LeaderboardSection from './LeaderboardSection';
import ReviewsSection from './ReviewsSection';
import ReviewSchema from './ReviewSchema';
// TEMPORARY: Android Tester Recruitment - Remove after 8 testers
import AndroidTesterModal from './game/AndroidTesterModal';

const HomePage = ({ onJoinGame }) => {
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [playerName, setPlayerName] = useState(() => localStorage.getItem('playerName') || '');
  const [gameId, setGameId] = useState('');
  const [playerCount, setPlayerCount] = useState(4); // Default to 4 players
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [connectionError, setConnectionError] = useState(false);
  const [showPlayerCountModal, setShowPlayerCountModal] = useState(false);
  const [, setJoiningGame] = useState(false);
  const joinTimeoutRef = useRef(null);
  const [showRulesModal, setShowRulesModal] = useState(false);

  // TEMPORARY: Android Tester Recruitment - Remove after 8 testers
  const [shouldShowAndroidTester, setShouldShowAndroidTester] = useState(false);
  const [showAndroidTesterModal, setShowAndroidTesterModal] = useState(false);
  const [androidTesterSubmitted, setAndroidTesterSubmitted] = useState(false);

  // Ensure modal is closed on initial load
  useEffect(() => {
    setShowPlayerCountModal(false);
  }, []);

  // Check for name in localStorage when component mounts
  useEffect(() => {
    const savedName = localStorage.getItem('playerName');
    if (savedName) {
      setPlayerName(savedName);
    }
    
    // Ensure modal is not showing on initial load
    setShowPlayerCountModal(false);
  }, []);

  // TEMPORARY: Android Tester Recruitment - Remove after 8 testers
  // Check if user should be prompted for Android tester signup
  useEffect(() => {
    // Only target Android browser users (not PWA)
    const isAndroid = /Android/i.test(navigator.userAgent);
    const isPWA = window.matchMedia('(display-mode: standalone)').matches;

    if (!isAndroid || isPWA) {
      setShouldShowAndroidTester(false);
      return;
    }

    // Check if user explicitly declined or already signed up
    const promptStatus = localStorage.getItem('qwixx_android_tester_prompted');
    if (promptStatus === 'skipped' || promptStatus === 'submitted' || promptStatus === 'recruitment_closed') {
      setShouldShowAndroidTester(false);
      return;
    }

    // Check server for available spots
    const checkAvailability = async () => {
      try {
        const serverUrl = process.env.REACT_APP_API_URL || localStorage.getItem('server_url') || 'http://localhost:4001';
        const response = await fetch(`${serverUrl}/api/android-testers/count`);
        const data = await response.json();

        if (data.success && data.recruiting) {
          // Show the popup - will keep showing until user clicks "Not interested" or signs up
          setShouldShowAndroidTester(true);
        } else {
          // Mark as recruitment closed
          localStorage.setItem('qwixx_android_tester_prompted', 'recruitment_closed');
          setShouldShowAndroidTester(false);
        }
      } catch (error) {
        // Silently fail - don't show popup if server unavailable
        setShouldShowAndroidTester(false);
      }
    };

    checkAvailability();
  }, []);

  // Function to handle player name input
  const handleNameChange = (e) => {
    // Store raw input in state
    setPlayerName(e.target.value);
  };

  // TEMPORARY: Android Tester Recruitment handlers - Remove after 8 testers
  const handleSkipAndroidTester = () => {
    localStorage.setItem('qwixx_android_tester_prompted', 'skipped');
    setShouldShowAndroidTester(false);
  };

  const handleCloseAndroidTester = () => {
    setShowAndroidTesterModal(false);
  };

  const handleAndroidTesterSuccess = () => {
    setAndroidTesterSubmitted(true);
    setShowAndroidTesterModal(false);
    setShouldShowAndroidTester(false);
  };

  // When player name changes, save to localStorage
  useEffect(() => {
    if (playerName) {
      // Sanitize before saving to localStorage
      localStorage.setItem('playerName', sanitizePlayerName(playerName));
    }
  }, [playerName]);

  // Handle PWA shortcut URL parameters and WhatsApp share links
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const action = params.get('action');
    const view = params.get('view');
    const source = params.get('source');
    const joinGameId = params.get('join');

    // Handle WhatsApp share link with ?join=GAMEID
    if (joinGameId) {
      // Auto-fill the game ID field
      setGameId(joinGameId.toUpperCase());

      // Focus and scroll to the join input
      setTimeout(() => {
        const joinInput = document.querySelector('input[placeholder*="game"]') ||
                         document.querySelector('input[name="gameId"]');
        if (joinInput) {
          joinInput.focus();
          joinInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);

      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
      return;
    }

    // Only process if coming from PWA shortcut or PWA launch
    if (source === 'shortcut' || source === 'pwa') {
      if (action === 'create') {
        // Trigger create game flow - show player count modal
        setShowPlayerCountModal(true);
      } else if (action === 'join') {
        // Focus the join game input
        setTimeout(() => {
          const joinInput = document.querySelector('input[placeholder*="game"]') ||
                           document.querySelector('input[name="gameId"]');
          if (joinInput) {
            joinInput.focus();
            joinInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 100);
      } else if (view === 'leaderboard') {
        // Scroll to leaderboard section
        setTimeout(() => {
          const leaderboardSection = document.getElementById('leaderboard') ||
                                     document.querySelector('.leaderboard-section');
          if (leaderboardSection) {
            leaderboardSection.scrollIntoView({ behavior: 'smooth' });
          }
        }, 100);
      }

      // Clean up URL (remove query params without refreshing)
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  // Fallback for when server is not available
  const handleFallbackMode = (mode = 'create', fallbackGameId = null) => {
    console.log('Using fallback mode for:', mode);
    
    // Generate a random game ID if needed
    const randomGameId = fallbackGameId || `GAME${Math.floor(Math.random() * 10000)}`;
    
    // Call the joining callback with offline parameters
    onJoinGame(randomGameId, playerName.trim(), 'friends', playerCount);
    
    setLoading(false);
    setShowPlayerCountModal(false);
  };

  const handleCreateGame = (count) => {
    if (!playerName.trim()) {
      setError('Please enter your name');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    // Sanitize inputs
    const trimmedName = sanitizePlayerName(playerName.trim());
    
    if (trimmedName === '') {
      setLoading(false);
      setError('Invalid player name');
      return;
    }
    
    // Use the passed count if provided, otherwise fallback to playerCount state
    const maxPlayersToUse = count || playerCount;
    
    if (socket && socket.connected) {
      console.log('Emitting createGame event to server with maxPlayers:', maxPlayersToUse);
      const options = {
        playerName: trimmedName,
        gameType: 'friends',
        maxPlayers: maxPlayersToUse
      };
      
      socket.timeout(10000).emit('createGame', options, (err, response) => {
        if (err || (response && !response.success)) {
          console.error('Error creating game:', err || (response ? response.error : 'Unknown error'));
          setLoading(false);
          setShowPlayerCountModal(false);
          setError((response && response.error) || err?.message || 'Failed to create game');
          
          // Fallback to offline mode if server connection is lost
          if (err && err.message === 'timeout') {
            console.log('Server timeout, switching to fallback mode');
            handleFallbackMode('create');
          }
        }
      });
    } else {
      // Fallback if socket isn't connected
      handleFallbackMode('create');
    }
  };
  
  const handleJoinGame = (e) => {
    e.preventDefault();
    
    // Validate inputs
    if (!playerName.trim()) {
      setError('Please enter your name');
      return;
    }
    
    if (!gameId.trim()) {
      setError('Please enter a game ID');
      return;
    }
    
    setLoading(true);
    setError(null);
    setJoiningGame(true);
    
    // Sanitize inputs
    const trimmedName = sanitizePlayerName(playerName.trim());
    const sanitizedGameId = gameId.trim().toUpperCase();
    
    if (trimmedName === '') {
      setLoading(false);
      setJoiningGame(false);
      setError('Invalid player name');
      return;
    }
    
    // Process the join request
    proceedWithJoin(sanitizedGameId);
  };

  // Separate function to handle the actual join process
  const proceedWithJoin = (sanitizedGameId) => {
    // Set a timeout to switch to fallback mode if the server doesn't respond
    joinTimeoutRef.current = setTimeout(() => {
      console.log('Server timeout for game join, switching to fallback mode');
      setJoiningGame(false);
      handleFallbackMode('join', sanitizedGameId);
    }, 8000);

    if (socket && socket.connected) {
      console.log('Emitting joinGame event to server');
      socket.timeout(8000).emit('joinGame', {
        gameId: sanitizedGameId,
        playerName: playerName.trim()
      }, (err, response) => {
        // Clear the timeout since we got a response
        if (joinTimeoutRef.current) {
          clearTimeout(joinTimeoutRef.current);
          joinTimeoutRef.current = null;
        }
        
        if (err || (response && !response.success)) {
          console.error('Error joining game:', err || (response ? response.error : 'Unknown error'));
          setLoading(false);
          setJoiningGame(false);
          
          // Map server error to translated error message
          let errorMessage = (response && response.error) || err?.message || 'Failed to join game';
          if (errorMessage.includes('Name already taken')) {
            errorMessage = t('errors.nameTaken');
          }
          setError(errorMessage);
          
          // Fallback to offline mode if server connection issue
          if (err && err.message === 'timeout') {
            console.log('Server timeout, switching to fallback mode');
            handleFallbackMode('join', sanitizedGameId);
          }
        } else {
          // Success from joinGame - IMPORTANT: This is the fix. The server doesn't emit gameJoined
          // but instead returns success in the callback. We need to handle this.
          console.log('[DEBUG-JOIN] Join success via callback with name:', playerName.trim());
          onJoinGame(sanitizedGameId, playerName.trim(), 'friends', response.maxPlayers || 4);
          setLoading(false);
          setJoiningGame(false);
        }
      });
    } else {
      // Clear timeout if we're going to fallback immediately
      if (joinTimeoutRef.current) {
        clearTimeout(joinTimeoutRef.current);
        joinTimeoutRef.current = null;
      }
      
      // Fallback if socket isn't connected
      handleFallbackMode('join', sanitizedGameId);
    }
  };

  // Clean up any pending timeouts
  useEffect(() => {
    return () => {
      if (joinTimeoutRef.current) {
        clearTimeout(joinTimeoutRef.current);
      }
    };
  }, []);

  const handleOpenCreateModal = () => {
    setShowPlayerCountModal(true);
  };
  
  const handleCloseModal = () => {
    setShowPlayerCountModal(false);
  };

  // Register event handlers on mount, clean up on unmount
  useEffect(() => {
    // Register event handlers
    socket.on('connect', () => {
      setConnectionError(false);
      setError('');
    });
    socket.on('connect_error', (err) => {
      setConnectionError(true);
      setError(`Server connection error. Running in offline mode.`);
    });
    socket.on('error', (err) => {
      setConnectionError(true);
      setError(`Socket error: ${err.message || 'Unknown error'}`);
    });
    socket.on('disconnect', (reason) => {
      const normalDisconnects = ['io client disconnect', 'io server disconnect', 'ping timeout'];
      if (!normalDisconnects.includes(reason)) {
        setConnectionError(true);
        setError(`Server connection lost: ${reason}. Running in offline mode.`);
      }
      if (reason === 'io server disconnect') {
        socket.connect();
      }
    });
    socket.on('gameCreated', (response) => {
      console.log('[DEBUG-JOIN] Game created response', response);
      setLoading(false);
      setShowPlayerCountModal(false);
      if (response.success) {
        const trimmedName = playerName.trim();
        console.log('[DEBUG-JOIN] Calling onJoinGame after gameCreated with:', {gameId: response.gameId, playerName: trimmedName, gameType: 'friends', maxPlayers: response.maxPlayers || playerCount});
        onJoinGame(response.gameId, trimmedName, 'friends', response.maxPlayers || playerCount);
      } else {
        setError(response.error || 'Failed to create game');
      }
    });
    socket.on('gameJoined', (response) => {
      console.log('[DEBUG-JOIN] Game joined response', response);
      setLoading(false);
      setJoiningGame(false);
      if (response.success) {
        const trimmedName = playerName.trim();
        console.log('[DEBUG-JOIN] Calling onJoinGame after gameJoined with:', {gameId: response.gameId, playerName: trimmedName, maxPlayers: response.maxPlayers || 4});
        onJoinGame(response.gameId, trimmedName, 'friends', response.maxPlayers || 4);
      } else {
        setError(response.error || 'Failed to join game');
      }
    });
    
    return () => {
      // Clean up event listeners
      socket.off('connect');
      socket.off('connect_error');
      socket.off('error');
      socket.off('disconnect');
      socket.off('gameCreated');
      socket.off('gameJoined');
    };
  }, [onJoinGame, playerCount, playerName]);

  // Functions to handle rules modal
  const openRulesModal = () => {
    setShowRulesModal(true);
  };
  
  const closeRulesModal = () => {
    setShowRulesModal(false);
  };

  return (
    <div className="container">
      <SEO language={language} />
      <div style={{ 
        backgroundColor: 'white', 
        borderRadius: '0.5rem', 
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)', 
        padding: '2rem',
        maxWidth: '500px',
        margin: '2rem auto',
        position: 'relative'
      }}>
        <div className="dice-header" style={{ position: 'relative' }}>
          {/* Language selector - positioned higher up */}
          <div style={{ 
            position: 'absolute',
            left: '-25px', 
            top: '-20px',
            transform: 'scale(0.625)',
            transformOrigin: 'center left',
            zIndex: 10 
          }}>
            <LanguageSelector />
          </div>
          
          {/* Information button - positioned opposite to language selector */}
          <div style={{ 
            position: 'absolute',
            right: '-15px', 
            top: '-15px',
            zIndex: 10 
          }}>
            <button 
              className="info-button" 
              onClick={openRulesModal}
              aria-label={t('gameRules.title')}
              style={{
                width: '20px',
                height: '20px',
                borderRadius: '50%',
                background: '#3b82f6',
                color: 'white',
                border: 'none',
                fontSize: '12px',
                fontWeight: 'bold',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
                transition: 'all 0.2s ease'
              }}
            >
              i
            </button>
          </div>
          
          <div className="dice-container">
            <div 
              className="die red"
              style={{ transition: 'transform 0.8s cubic-bezier(0.175, 0.885, 0.32, 1.275)' }}
              onMouseEnter={(e) => e.currentTarget.style.transform = 'rotateY(360deg)'}
              onMouseLeave={(e) => e.currentTarget.style.transform = 'rotateY(0deg)'}
            >
              <div className="die-face die-front one">
                <div className="die-dots">
                  <div className="dot"></div>
                </div>
              </div>
              <div className="die-face die-back six">
                <div className="die-dots">
                  <div className="dot"></div>
                  <div className="dot"></div>
                  <div className="dot"></div>
                  <div className="dot"></div>
                  <div className="dot"></div>
                  <div className="dot"></div>
                </div>
              </div>
              <div className="die-face die-top two">
                <div className="die-dots">
                  <div className="dot"></div>
                  <div className="dot"></div>
                </div>
              </div>
              <div className="die-face die-bottom five">
                <div className="die-dots">
                  <div className="dot"></div>
                  <div className="dot"></div>
                  <div className="dot"></div>
                  <div className="dot"></div>
                  <div className="dot"></div>
                </div>
              </div>
              <div className="die-face die-left four">
                <div className="die-dots">
                  <div className="dot"></div>
                  <div className="dot"></div>
                  <div className="dot"></div>
                  <div className="dot"></div>
                </div>
              </div>
              <div className="die-face die-right three">
                <div className="die-dots">
                  <div className="dot"></div>
                  <div className="dot"></div>
                  <div className="dot"></div>
                </div>
              </div>
            </div>
            
            <div 
              className="die yellow"
              style={{ transition: 'transform 0.8s cubic-bezier(0.175, 0.885, 0.32, 1.275)' }}
              onMouseEnter={(e) => e.currentTarget.style.transform = 'rotateX(360deg)'}
              onMouseLeave={(e) => e.currentTarget.style.transform = 'rotateX(0deg)'}
            >
              <div className="die-face die-front two">
                <div className="die-dots">
                  <div className="dot"></div>
                  <div className="dot"></div>
                </div>
              </div>
              <div className="die-face die-back five">
                <div className="die-dots">
                  <div className="dot"></div>
                  <div className="dot"></div>
                  <div className="dot"></div>
                  <div className="dot"></div>
                  <div className="dot"></div>
                </div>
              </div>
              <div className="die-face die-top six">
                <div className="die-dots">
                  <div className="dot"></div>
                  <div className="dot"></div>
                  <div className="dot"></div>
                  <div className="dot"></div>
                  <div className="dot"></div>
                  <div className="dot"></div>
                </div>
              </div>
              <div className="die-face die-bottom one">
                <div className="die-dots">
                  <div className="dot"></div>
                </div>
              </div>
              <div className="die-face die-left three">
                <div className="die-dots">
                  <div className="dot"></div>
                  <div className="dot"></div>
                  <div className="dot"></div>
                </div>
              </div>
              <div className="die-face die-right four">
                <div className="die-dots">
                  <div className="dot"></div>
                  <div className="dot"></div>
                  <div className="dot"></div>
                  <div className="dot"></div>
                </div>
              </div>
            </div>
            
            <div 
              className="die green"
              style={{ transition: 'transform 0.8s cubic-bezier(0.175, 0.885, 0.32, 1.275)' }}
              onMouseEnter={(e) => e.currentTarget.style.transform = 'rotateZ(360deg)'}
              onMouseLeave={(e) => e.currentTarget.style.transform = 'rotateZ(0deg)'}
            >
              <div className="die-face die-front three">
                <div className="die-dots">
                  <div className="dot"></div>
                  <div className="dot"></div>
                  <div className="dot"></div>
                </div>
              </div>
              <div className="die-face die-back four">
                <div className="die-dots">
                  <div className="dot"></div>
                  <div className="dot"></div>
                  <div className="dot"></div>
                  <div className="dot"></div>
                </div>
              </div>
              <div className="die-face die-top one">
                <div className="die-dots">
                  <div className="dot"></div>
                </div>
              </div>
              <div className="die-face die-bottom six">
                <div className="die-dots">
                  <div className="dot"></div>
                  <div className="dot"></div>
                  <div className="dot"></div>
                  <div className="dot"></div>
                  <div className="dot"></div>
                  <div className="dot"></div>
                </div>
              </div>
              <div className="die-face die-left five">
                <div className="die-dots">
                  <div className="dot"></div>
                  <div className="dot"></div>
                  <div className="dot"></div>
                  <div className="dot"></div>
                  <div className="dot"></div>
                </div>
              </div>
              <div className="die-face die-right two">
                <div className="die-dots">
                  <div className="dot"></div>
                  <div className="dot"></div>
                </div>
              </div>
            </div>
            
            <div 
              className="die blue"
              style={{ transition: 'transform 0.8s cubic-bezier(0.175, 0.885, 0.32, 1.275)' }}
              onMouseEnter={(e) => e.currentTarget.style.transform = 'rotateX(180deg) rotateY(180deg)'}
              onMouseLeave={(e) => e.currentTarget.style.transform = 'rotateX(0deg) rotateY(0deg)'}
            >
              <div className="die-face die-front four">
                <div className="die-dots">
                  <div className="dot"></div>
                  <div className="dot"></div>
                  <div className="dot"></div>
                  <div className="dot"></div>
                </div>
              </div>
              <div className="die-face die-back three">
                <div className="die-dots">
                  <div className="dot"></div>
                  <div className="dot"></div>
                  <div className="dot"></div>
                </div>
              </div>
              <div className="die-face die-top five">
                <div className="die-dots">
                  <div className="dot"></div>
                  <div className="dot"></div>
                  <div className="dot"></div>
                  <div className="dot"></div>
                  <div className="dot"></div>
                </div>
              </div>
              <div className="die-face die-bottom two">
                <div className="die-dots">
                  <div className="dot"></div>
                  <div className="dot"></div>
                </div>
              </div>
              <div className="die-face die-left six">
                <div className="die-dots">
                  <div className="dot"></div>
                  <div className="dot"></div>
                  <div className="dot"></div>
                  <div className="dot"></div>
                  <div className="dot"></div>
                  <div className="dot"></div>
                </div>
              </div>
              <div className="die-face die-right one">
                <div className="die-dots">
                  <div className="dot"></div>
                </div>
              </div>
            </div>
          </div>
          <h1 className="game-title">Qwixx Online</h1>
          <p className="tagline">{t('homepage.tagline')}</p>
        </div>

        {/* TEMP: virtual-player (AI) feature announcement — safe to remove later */}
        <div style={{
          backgroundColor: '#ecfeff',
          border: '1px solid #06b6d4',
          color: '#155e63',
          padding: '0.75rem 1rem',
          borderRadius: '0.5rem',
          marginBottom: '1rem',
          textAlign: 'center',
          fontWeight: 600
        }}>
          {t('homepage.botAnnouncement')}
        </div>

        {connectionError && (
          <div style={{ 
            backgroundColor: '#f8d7da', 
            color: '#721c24', 
            padding: '0.75rem', 
            borderRadius: '0.375rem', 
            marginBottom: '1rem', 
            textAlign: 'center',
            fontWeight: 'bold'
          }}>
            {error}
          </div>
        )}
        
        {error && !connectionError && (
          <div style={{ 
            backgroundColor: '#fee2e2', 
            color: '#b91c1c', 
            padding: '0.75rem', 
            borderRadius: '0.375rem', 
            marginBottom: '1rem' 
          }}>
            {error}
          </div>
        )}
        
        <div style={{ marginBottom: '1.5rem', textAlign: 'center' }}>
          {/* Enter Your Name heading */}
          <div style={{ 
            textAlign: 'center', 
            marginBottom: '1rem', 
            fontWeight: 'bold',
            fontSize: '1.25rem',
            background: 'linear-gradient(to right, #ef4444, #f59e0b, #10b981, #3b82f6)',
            WebkitBackgroundClip: 'text',
            backgroundClip: 'text',
            color: 'transparent'
          }}>
            {t('homepage.enterYourName')}
          </div>
          
          {/* Player Name (Name section) */}
          <div style={{ marginBottom: '1.5rem' }}>
            <input
              id="playerName"
              type="text"
              value={playerName}
              onChange={handleNameChange}
              placeholder={t('homepage.playerNamePlaceholder')}
              disabled={loading}
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '1px solid #d1d5db',
                borderRadius: '0.375rem',
                fontSize: '1rem'
              }}
            />
          </div>
          
          {/* Start New Game section with centered heading and button */}
          <div style={{ 
            textAlign: 'center', 
            marginBottom: '1.5rem',
            padding: '1rem',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            border: '2px solid transparent',
            borderRadius: '0.5rem',
            backgroundImage: 'linear-gradient(white, white), linear-gradient(to right, #ef4444, #f59e0b, #10b981, #3b82f6)',
            backgroundOrigin: 'border-box',
            backgroundClip: 'padding-box, border-box'
          }}>
            <div style={{ 
              marginBottom: '1rem', 
              fontWeight: 'bold',
              fontSize: '1.25rem',
              background: 'linear-gradient(to right, #ef4444, #f59e0b, #10b981, #3b82f6)',
              WebkitBackgroundClip: 'text',
              backgroundClip: 'text',
              color: 'transparent'
            }}>
              {t('homepage.startNewGame')}
            </div>
            
            <div style={{ width: '100%', maxWidth: '95%', margin: '0 auto', display: 'flex', justifyContent: 'center' }}>
              <button 
                onClick={handleOpenCreateModal}
                disabled={loading}
                className="button create-game"
                style={{
                  backgroundColor: loading ? '#9ca3af' : '#4f46e5',
                  color: 'white',
                  border: 'none',
                  borderRadius: '0.375rem',
                  padding: '0.75rem 1.5rem',
                  fontSize: '1rem',
                  fontWeight: 'bold',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  width: '100%'
                }}
              >
                {loading && showPlayerCountModal ? 'Creating...' : t('homepage.createGame')}
              </button>
            </div>
          </div>
          
          <div style={{ width: '100%', textAlign: 'center', margin: '1.5rem 0', color: '#6b7280' }}>
            - {t('common.or')} -
          </div>
          
          {/* Join a Friend's Game section with centered heading and form */}
          <div style={{ 
            textAlign: 'center', 
            marginBottom: '1rem',
            padding: '1rem',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            border: '2px solid transparent',
            borderRadius: '0.5rem',
            backgroundImage: 'linear-gradient(white, white), linear-gradient(to right, #ef4444, #f59e0b, #10b981, #3b82f6)',
            backgroundOrigin: 'border-box',
            backgroundClip: 'padding-box, border-box'
          }}>
            <div style={{ 
              textAlign: 'center', 
              marginBottom: '1rem', 
              fontWeight: 'bold',
              fontSize: '1.25rem',
              background: 'linear-gradient(to right, #ef4444, #f59e0b, #10b981, #3b82f6)',
              WebkitBackgroundClip: 'text',
              backgroundClip: 'text',
              color: 'transparent'
            }}>
              {t('homepage.joinFriendsGame')}
            </div>
            
            <form onSubmit={handleJoinGame} style={{ width: '100%', maxWidth: '95%', margin: '0 auto' }}>
              <div style={{ marginBottom: '1.5rem', display: 'flex', justifyContent: 'center' }}>
                <input
                  id="gameId"
                  type="text"
                  value={gameId}
                  onChange={(e) => setGameId(e.target.value)}
                  placeholder={t('homepage.gameIdPlaceholder')}
                  disabled={loading}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '0.375rem',
                    fontSize: '1rem',
                    textTransform: 'uppercase'
                  }}
                />
              </div>
              
              <div style={{ display: 'flex', justifyContent: 'center' }}>
                <button 
                  type="submit" 
                  disabled={loading}
                  className="button join-game"
                  style={{
                    backgroundColor: loading ? '#9ca3af' : '#10b981',
                    color: 'white',
                    border: 'none',
                    borderRadius: '0.375rem',
                    padding: '0.75rem 1.5rem',
                    fontSize: '1rem',
                    fontWeight: 'bold',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    width: '100%'
                  }}
                >
                  {loading && !showPlayerCountModal ? 'Joining...' : t('homepage.joinGame')}
                </button>
              </div>
            </form>
          </div>

          {/* Help text for new players */}
          <div style={{
            textAlign: 'center',
            marginTop: '1.5rem',
            padding: '0.75rem',
            fontSize: '0.875rem',
            color: '#6b7280',
            fontStyle: 'italic',
            lineHeight: '1.5'
          }}>
            {t('homepage.newPlayerHelp')}
          </div>
        </div>
      </div>

      {/* TEMPORARY: Android Tester Recruitment - Remove after 8 testers */}
      {/* Android Tester Prompt - Only for Android browser users */}
      {shouldShowAndroidTester && !androidTesterSubmitted && !showAndroidTesterModal && (
        <div style={{
          background: 'linear-gradient(135deg, #3ddc84 0%, #1a73e8 100%)',
          borderRadius: '0.75rem',
          padding: '1.5rem',
          margin: '1.5rem auto',
          maxWidth: '500px',
          textAlign: 'center',
          color: 'white',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
        }}>
          <p style={{ fontSize: '1.1rem', marginBottom: '1rem', fontWeight: '500' }}>
            🤖 {t('androidTester.prompt')}
          </p>
          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
            <button
              onClick={() => setShowAndroidTesterModal(true)}
              style={{
                backgroundColor: 'white',
                color: '#1a73e8',
                padding: '0.75rem 1.5rem',
                borderRadius: '0.5rem',
                fontWeight: '600',
                border: 'none',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                fontSize: '1rem'
              }}
            >
              {t('androidTester.signUp')}
            </button>
            <button
              onClick={handleSkipAndroidTester}
              style={{
                backgroundColor: 'transparent',
                color: 'white',
                padding: '0.75rem 1.5rem',
                borderRadius: '0.5rem',
                fontWeight: '500',
                border: '2px solid white',
                cursor: 'pointer',
                transition: 'all 0.3s ease'
              }}
            >
              {t('androidTester.notInterested')}
            </button>
          </div>
        </div>
      )}

      {/* Android Tester Modal */}
      {showAndroidTesterModal && (
        <AndroidTesterModal
          isOpen={showAndroidTesterModal}
          onClose={handleCloseAndroidTester}
          onSuccess={handleAndroidTesterSuccess}
          playerName={playerName}
        />
      )}

      {/* Success message after Android tester signup */}
      {androidTesterSubmitted && (
        <div style={{
          background: '#3ddc84',
          borderRadius: '0.75rem',
          padding: '1rem',
          margin: '1.5rem auto',
          maxWidth: '500px',
          textAlign: 'center',
          color: 'white',
          fontWeight: '500',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
        }}>
          ✅ {t('androidTester.thankYou')}
        </div>
      )}

      {/* Weekly Leaderboard */}
      <LeaderboardSection />

      {/* Player Reviews Section */}
      <ReviewsSection />

      {/* SEO Schema for Reviews */}
      <ReviewSchema />

      {/* Main content container for FAQ and footer */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '0.5rem',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
        padding: '2rem',
        maxWidth: '500px',
        margin: '1rem auto'
      }}>
        {/* FAQ Link */}
        <div style={{ textAlign: 'center', margin: '1rem 0 0.5rem', fontSize: '0.875rem' }}>
          <button
            onClick={() => navigate('/faq')}
            style={{
              background: 'none',
              border: 'none',
              color: '#3b82f6',
              textDecoration: 'underline',
              cursor: 'pointer',
              fontSize: '0.875rem'
            }}
          >
            {t('faq.title')}
          </button>
        </div>

        {/* Copyright notice */}
        <div style={{ textAlign: 'center', margin: '0.5rem 0', fontSize: '0.875rem', color: '#6b7280' }}>
          {t('common.copyright')}
        </div>
        
        {/* Add disclaimer */}
        <div className="disclaimer-container">
          <span className="disclaimer-trigger">{t('disclaimer.title')}</span>
          <div className="disclaimer-content">
            {t('disclaimer.content')}
          </div>
        </div>

        {/* Privacy Policy Link */}
        <div style={{ textAlign: 'center', marginTop: '0.5rem', fontSize: '0.875rem' }}>
          <button
            onClick={() => navigate('/privacy')}
            style={{
              background: 'none',
              border: 'none',
              color: '#3b82f6',
              textDecoration: 'underline',
              cursor: 'pointer',
              fontSize: '0.875rem'
            }}
          >
            {t('privacy.title')}
          </button>
        </div>
      </div>

      {/* Player Count Modal */}
      {showPlayerCountModal && (
        <PlayerCountModal
          onClose={handleCloseModal}
          onSelectCount={(count) => {
            setPlayerCount(count);
            handleCreateGame(count);
          }}
          isLoading={loading}
        />
      )}

      {/* Rules Modal */}
      {showRulesModal && (
        <div className="modal-overlay">
          <div className="rules-modal">
            <div className="rules-header">
              <h2 style={{ 
                textAlign: 'center', 
                marginBottom: '0.5rem',
                background: 'linear-gradient(to right, #ef4444, #f59e0b, #10b981, #3b82f6)',
                WebkitBackgroundClip: 'text',
                backgroundClip: 'text',
                color: 'transparent',
                textShadow: '2px 2px 4px rgba(0, 0, 0, 0.1)',
                fontSize: '1.75rem',
                fontWeight: '700',
                position: 'relative',
                display: 'inline-block',
                padding: '0 1rem'
              }}>
                {t('gameRules.title')}
              </h2>
              <button 
                className="close-btn" 
                onClick={closeRulesModal}
                style={{
                  position: 'absolute',
                  top: '15px',
                  right: '15px',
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#6b7280',
                  zIndex: 2,
                  padding: '5px 10px'
                }}
              >
                &times;
              </button>
            </div>
            <div className="rules-content">
              <p>{t('gameRules.overview')}</p>
              
              {/* Dynamically display all game rule sections */}
              {Array.from({ length: 9 }, (_, i) => (
                <React.Fragment key={i}>
                  <h3>{t(`gameRules.sections.${i}.title`)}</h3>
                  <p>{t(`gameRules.sections.${i}.content`)}</p>
                </React.Fragment>
              ))}

              {/* Donation section - Temporarily disabled (no brand license yet) */}
              {/* <div style={{
                marginTop: '2rem',
                textAlign: 'center',
                padding: '1rem',
                borderTop: '2px solid #e5e7eb'
              }}>
                <p style={{
                  fontSize: '1.1rem',
                  fontWeight: '600',
                  marginBottom: '1rem',
                  color: '#374151'
                }}>
                  {t('gameRules.donation')}
                </p>
                <img
                  src="/bmc_qr.png"
                  alt="Scan QR code to support Qwixx Online with a coffee donation"
                  style={{
                    maxWidth: '200px',
                    height: 'auto',
                    margin: '0 auto',
                    display: 'block'
                  }}
                />
              </div> */}
            </div>
          </div>
        </div>
      )}

      <SEOFooter />
    </div>
  );
};

export default HomePage;